import React, { useState, useContext, useEffect } from 'react';
import { NavBar, Tabs, List, Skeleton, ErrorBlock, Badge, Divider } from 'antd-mobile';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import { getWorkOrderAccessPanel, updateWorkOrderAccessPanel } from '../api';
import Signatures from '../components/Signatures';
import InputComponent from '../components/Input';
import Files from './Files';
import Notes from './Notes';
import { featureEnabled, matchesPermission } from '../utils/permission-helpers';
import UserSelfContext from '../utils/self-context';

const AccessPanelWrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-flow: column;
`;
const ContentWrapper = styled.div`
	flex: 1;
	min-height: 0;
	overflow:  auto;
	width: 100%;
	background: #f0f2f5;
`;

const Overview = styled.div`
	padding: 12px 0px;
`;

const Title = styled.h2`
	display: inline-block;
	color: var(--adm-color-text);
	flex: 1;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const AccessPanelInfo = styled.div`
	padding: 0px 12px;
	background: #fff;

	.adm-list-item-title {
		margin-bottom: 8px;
		display: flex;
		align-items: center;
	}

	h2 {
		margin: 0px 8px 0px 0px;
		display: inline-block;
		color: var(--adm-color-text);
	}

	.adm-ellipsis {
		a {
			margin-left: 8px;
		}
	}
`;

const NotFound = styled(ErrorBlock)`
	height: 100%;
	padding: 12px;
	justify-content: center;
	align-items: center;
	display: flex;
	flex-direction: column;
`;

const Nav = styled(NavBar)`
	background: #fff;
	display: flex;
	border-bottom: solid 1px var(--adm-color-border);
`;

const Description = styled(List.Item)`
	font-size: var(--adm-font-size-9);
`;

const TabBar = styled(Tabs)`
	display: flex;
	flex-direction: column;
	height: 100%;
	.adm-tabs-header {
		background: #fff;
	}

	.adm-tabs-content {
		flex: 1;
		overflow: auto;
		padding: 0;
	}
`;

function AccessPanel() {
	const params = useParams();
	const history = useHistory();
	const { id, workOrderId } = params;
	const { userSelf } = useContext(UserSelfContext);
	const [loading, setLoading] = useState(false);
	const [err, setErr] = useState(false);
	const [saving, setSaving] = useState(false);
	const [accessPanel, setAccessPanel] = useState({});

	async function fetchAccessPanel(silent) {
		if (!silent) {
			setLoading(true);
		}

		try {
			const data = await getWorkOrderAccessPanel(workOrderId, id);
			setAccessPanel(data);
		} catch (e) {
			setErr(true);
		}

		setLoading(false);
	}

	useEffect(() => {
		fetchAccessPanel();
	}, []);

	async function updateAccessPanel(data) {
		const payload = data;
		payload.id = accessPanel.id;

		try {
			await updateWorkOrderAccessPanel(workOrderId, data);
			fetchAccessPanel(true);
		} catch (e) {
			setErr(true);
		}
	}

	const staffEnabled = featureEnabled(userSelf, 'work_order_staff_enabled');
	const hasClosedSignature = accessPanel.signatures?.find(s => s.object_type === 'access_panel_close');
	const hasOpenedSignature = accessPanel.signatures?.find(s => s.object_type === 'access_panel_open');
	const hasInspectedSignature = accessPanel.signatures?.find(s => s.object_type === 'access_panel_inspection');

	return (err ? <NotFound
		title="Access panel not found"
		description="This access panel does not exist, or you dont have rights to view it"
		status='empty'
	/> : <AccessPanelWrapper>
		<Nav onBack={() => history.push(`/work-order/${workOrderId}`)} back={''}>
			Access Panel
		</Nav>
		<AccessPanelInfo>
			{loading ? <Skeleton.Paragraph lineCount={1} animated/> :
				<Description
					title={<Title>{accessPanel.name || '-'}</Title>}
				>
					
				</Description>
			}
		</AccessPanelInfo>
		<ContentWrapper>
			<TabBar>
				<Tabs.Tab
					title={'Overview'}
					key='overview'
				>
					<Overview>
						<InputComponent
							onSave={updateAccessPanel}
							disabled={!matchesPermission(userSelf, 'sign_work_order_access_panels')}
							dataKey="opened_at"
							isRequired={true}
							allowClear={true}
							title="Opened date/time"
							type="datetime"
							initialValue={accessPanel.opened_at ? new Date(accessPanel.opened_at) : null}
						/>
						{staffEnabled && <Signatures
							onChange={async (id, removed) => {
								if (!removed) {
									await updateAccessPanel({
										opened_at: moment()
									});
								}

								fetchAccessPanel(true);
							}}
							actionText={'Mark as Opened'}
							doneText="Opened by"
							emptyText={'Not opened yet...'}
							isDisabled={!matchesPermission(userSelf, 'sign_work_order_access_panels') || hasClosedSignature}
							item={accessPanel}
							objectType={'access_panel_open'}
						/>}
						<Divider style={{ marginTop: 32 }} />
						<InputComponent
							onSave={updateAccessPanel}
							allowClear={true}
							isDisabled={!matchesPermission(userSelf, 'sign_work_order_access_panels')}
							dataKey="closed_at"
							isRequired={true}
							title="Closed date/time"
							type="datetime"
							initialValue={accessPanel.closed_at ? new Date(accessPanel.closed_at) : null}
						/>
						{staffEnabled && <Signatures
							onChange={async (id, removed) => {
								if (!removed) {
									await updateAccessPanel({
										closed_at: moment()
									});
								}

								fetchAccessPanel(true);
							}}
							actionText={'Mark as Closed'}
							doneText="Closed by"
							emptyText={'Not closed yet...'}
							isDisabled={!matchesPermission(userSelf, 'sign_work_order_access_panels') || (!hasOpenedSignature || hasInspectedSignature)}
							item={accessPanel}
							objectType={'access_panel_close'}
						/>}
						<Divider style={{ marginTop: 32 }} />
						<InputComponent
							onSave={updateAccessPanel}
							disabled={!matchesPermission(userSelf, 'sign_work_order_access_panels')}
							dataKey="inspected_at"
							isRequired={true}
							allowClear={true}
							title="Inspected date/time"
							type="datetime"
							initialValue={accessPanel.inspected_at ? new Date(accessPanel.inspected_at) : null}
						/>
						{staffEnabled && <Signatures
							onChange={async (id, removed) => {
								if (!removed) {
									await updateAccessPanel({
										inspected_at: moment()
									});
								}

								fetchAccessPanel(true);
							}}
							actionText={'Mark as Inspected'}
							doneText="Inspected by"
							emptyText={'Not inspected yet...'}
							isDisabled={!matchesPermission(userSelf, 'sign_work_order_access_panels') || !hasClosedSignature}
							item={accessPanel}
							objectType={'access_panel_inspection'}
						/>}
					</Overview>
				</Tabs.Tab>
				<Tabs.Tab
					title={<Badge
						content={accessPanel.files_count || null}
						style={{ '--color': 'var(--adm-color-primary)', '--right': '-10px', '--top': '4px' }}>
							Files
					</Badge>}
					key='files'
				>
					<Files
						onChange={() => fetchAccessPanel(true)}
						objectType="access_panel"
						disabled={!matchesPermission(userSelf, 'manage_work_order_access_panels_files_notes')}
						objectId={accessPanel.id}
					/>
				</Tabs.Tab>
				<Tabs.Tab
					title={<Badge
						content={accessPanel.notes_count || null}
						style={{ '--color': 'var(--adm-color-primary)', '--right': '-10px', '--top': '4px' }}>
							Notes
					</Badge>}
					key='notes'
				>
					<Notes
						onChange={() => fetchAccessPanel(true)}
						objectType="access_panel"
						disabled={!matchesPermission(userSelf, 'manage_work_order_access_panels_files_notes')}
						objectId={accessPanel.id}
					/>
				</Tabs.Tab>
			</TabBar>
		</ContentWrapper>
	</AccessPanelWrapper>);
}

export default AccessPanel;
